import { useState } from 'react';
import { Microphone, Square } from '@phosphor-icons/react';
import { m, useReducedMotion } from 'motion/react';
import { cn } from '../lib/cn';
import { TapIcon } from './icons/TapIcon';

export interface MicCircleProps {
  /** Recording: a dark circle with a pulsing ring; idle: the lime mic (the screen's one lime). */
  recording?: boolean;
  /** Recording with a stop square instead of the mic (a tap stops). */
  stopIcon?: boolean;
  label: string;
  disabled?: boolean;
  onClick: () => void;
}

/** The 72 px round mic of the recorders. */
export function MicCircle({ recording = false, stopIcon = false, label, disabled, onClick }: MicCircleProps) {
  const reduced = useReducedMotion();
  const [taps, setTaps] = useState(0);
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={() => {
        setTaps((n) => n + 1);
        onClick();
      }}
      className={cn(
        'relative inline-flex items-center justify-center w-[72px] h-[72px] flex-shrink-0 rounded-full',
        'transition-[transform,opacity,background-color] duration-150 active:scale-95 disabled:opacity-50',
        recording ? 'bg-ink text-ink-on' : 'bg-accent text-accent-on',
      )}
    >
      {recording && !reduced && (
        <m.span
          aria-hidden
          className="absolute inset-0 rounded-full bg-ink"
          initial={{ scale: 1, opacity: 0.5 }}
          animate={{ scale: 1.25, opacity: 0 }}
          transition={{ duration: 1.2, repeat: Infinity, ease: 'easeOut' }}
        />
      )}
      <span className="relative inline-flex">
        {recording && stopIcon ? (
          <Square size={28} weight="fill" />
        ) : (
          <TapIcon icon={Microphone} size={32} weight={recording ? 'fill' : 'regular'} playKey={taps} />
        )}
      </span>
    </button>
  );
}
